import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactMapGL, { MapRef, ViewState } from "react-map-gl";
import { useTranslation } from "react-i18next";
import styles from "../../main/ObjectDetails/ObjectDetails.module.scss";
import AnimatedDetailsContainer from "../../../../share/ResultDetails/AnimatedDetailsContainer/AnimatedDetailsContainer";
import DetailsHeader from "../../../../share/ResultDetails/DetailsHeader/DetailsHeader";
import Details from "./ActiveTabs/Details";
import Contacts from "./ActiveTabs/Contacts";
import CustomMarker from "../../_mapComponents/CustomMarker/CustomMarker";
import { ICurrentMarker } from "../../../../models/CurrentMarker";
import { CurrentCompany } from "../../../../models/CurrentCompany";
import {
  defaultViewport,
  mapboxAccessToken,
} from "../../../../constants/mapConstants";
import util from "../../../../util";
import useSelectedLanguage from "../../../../hooks/useSelectedLanguage";
import Jp from "../../../../assets/svg/jpIcon.svg";

interface ListViewCompanyDetailsProps {
  company: CurrentCompany;
  isDetailsActive: boolean;
  onClose: () => void;
}

const COMPANY_MAP_STYLE = "mapbox://styles/aimap/clcad3pwq001a14niei1q701i";

const ListViewCompanyDetails: React.FC<ListViewCompanyDetailsProps> = ({
  company,
  isDetailsActive,
  onClose,
}) => {
  const selectedLanguage = useSelectedLanguage();
  const { t: tObjectDetails } = useTranslation("translation", {
    keyPrefix: "mainPage.mapFilters.objectDetails",
  });

  const mapRef = useRef<MapRef | null>(null);
  const [viewState, setViewState] = useState<ViewState>(() => {
    const initialLat = Number(company?.lon);
    const initialLon = Number(company?.lat);
    const hasInitialCoordinates =
      Number.isFinite(initialLat) && Number.isFinite(initialLon);

    return {
      latitude: hasInitialCoordinates ? initialLat : defaultViewport.latitude,
      longitude: hasInitialCoordinates ? initialLon : defaultViewport.longitude,
      zoom: hasInitialCoordinates ? 13 : defaultViewport.zoom,
      bearing: 0,
      pitch: 0,
    };
  });

  useEffect(() => {
    const rawLat = Number(company?.lon);
    const rawLon = Number(company?.lat);
    const hasCoordinates = Number.isFinite(rawLat) && Number.isFinite(rawLon);

    setViewState((prev) => ({
      ...prev,
      latitude: hasCoordinates ? rawLat : defaultViewport.latitude,
      longitude: hasCoordinates ? rawLon : defaultViewport.longitude,
      zoom: hasCoordinates ? 13 : defaultViewport.zoom,
    }));
  }, [company?.lat, company?.lon]);

  const marker = useMemo<ICurrentMarker>(() => {
    const latitude = Number(company?.lon);
    const longitude = Number(company?.lat);

    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      return { lat: latitude, lon: longitude };
    }

    return null;
  }, [company?.lat, company?.lon]);

  const handleMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap?.();
    if (map) {
      util.mapInterfaceUtil.changeMapboxLanguage(map, selectedLanguage);
      util.mapInterfaceUtil.addPinIconToMapbox(map);
    }
  }, [selectedLanguage]);

  const description = company?.ADDRESS || "";
  const title = company?.SHORT_NAME || company?.NAME || "";

  return (
    <AnimatedDetailsContainer isShown={isDetailsActive}>
      <DetailsHeader
        title={title}
        description={description}
        closeDetails={onClose}
        logo={<img src={Jp} alt="" />}
      />
      <div className={styles.listViewDetailsLayout}>
        <div className={styles.listViewMapWrapper}>
          <ReactMapGL
            {...viewState}
            ref={(instance) => (mapRef.current = instance)}
            mapboxAccessToken={mapboxAccessToken}
            mapStyle={COMPANY_MAP_STYLE}
            onMove={(event) => setViewState(event.viewState)}
            dragRotate={false}
            doubleClickZoom={false}
            scrollZoom={false}
            attributionControl={false}
            style={{ width: "100%", height: "100%" }}
            onLoad={handleMapLoad}
          >
            {marker && <CustomMarker marker={marker} />}
          </ReactMapGL>
        </div>
        <div className={styles.listViewPanels}>
          <section className={styles.listViewPanel}>
            <h3 className={styles.listViewPanelTitle}>
              {tObjectDetails("details.title")}
            </h3>
            <Details
              key={`details-${company?.RECORD ?? company?.NAME}`}
              contentClassName={styles.listViewPanelContent}
            />
          </section>
          <section className={styles.listViewPanel}>
            <h3 className={styles.listViewPanelTitle}>
              {tObjectDetails("contacts.title")}
            </h3>
            <Contacts
              key={`contacts-${company?.RECORD ?? company?.NAME}`}
              contentClassName={styles.listViewPanelContent}
            />
          </section>
        </div>
      </div>
    </AnimatedDetailsContainer>
  );
};

export default ListViewCompanyDetails;
